import { Injectable } from '@angular/core';
import { Action, NgxsOnInit, Selector, State, StateContext, Store, createSelector } from '@ngxs/store';
import { PageAction } from '@progress/kendo-angular-grid/dist/es2015/scrolling/scroller.service';
import _, { concat } from 'lodash';
import { filter, tap } from 'rxjs/operators';
import { SkillConstants } from 'src/app/store/samwin/samwin-skills/skill-constants';
import { AgentGreetingAssignmentRemoteDto as AgentGreetingAssignment } from 'src/generated/signalR/agent-greeting-assignment-remote-dto';
import { AgentGreetingWaveRemoteDto as AgentGreetingWave } from 'src/generated/signalR/agent-greeting-wave-remote-dto';
import {
   AgentGreetingAssignmentRemoteDto,
   AgentGreetingRemoteDto,
   AgentGreetingWaveRemoteDto,
   UpdateAgentGreetingsRequestData,
   UsersService,
} from 'src/generated/swagger';
import {
   AddGreetingItem,
   AddGreetingItems,
   AddGreetingWave,
   PersistAgentGreetings,
   RemoveGreetingItem,
   RemoveGreetingItems,
   RemoveGreetingWave,
   SetAgentGreetingDraggedItemKey,
   SetAgentGreetingIsIciDisplayGrouped,
   SetAgentGreetingPage,
   SetAgentGreetingSkillFilter,
   SetAgentGreetingsDraftSettings,
   SetAgentGreetingsIsInEditing,
   SetAgentGreetingsSettings,
   SetWasValuePersisted,
   UpdateAgentGreetingItems,
   UpdateAgentGreetingWavesFromServer,
   UpdateAgentGreetingsFromServer,
   UpdateGreetingWaves,
} from './agent-greeting-settings.actions';

export interface AgentGreetingSettingsStateModel {
   wasValuePersisted: boolean;
   filterValue: string;
   isInEditing: boolean;
   draggedItemKey: AudioFileInfo;
   currentPage: PageAction;
   isIciDisplayGrouped: boolean;
   currentSetting: AgentGreetingSettings;
   draftSettings: AgentGreetingSettings;
}

export interface AgentGreetingSettings {
   agentGreetingItems: AgentGreetingItem[];
   agentGreetingWaves: AgentGreetingWave[];
}

export interface AgentGreetingItem {
   key: AgentGreetingAssignment;
   waveId: number;
}

export interface AgentGreetingItemExpandedState {
   group: AgentGreetingAssignment;
   isExpanded: boolean;
}

export interface AgentGreetingConfig {
   assignment: AgentGreetingItem;
   wave: AudioFileInfo;
}

export interface AudioFileInfo {
   id: number;
   name: string;
}

const EMPTY_SETTING = { agentGreetingItems: [], agentGreetingWaves: [] };

@State<AgentGreetingSettingsStateModel>({
   name: 'agentGreetingSettingsState',
   defaults: {
      wasValuePersisted: false,
      filterValue: null,
      isInEditing: false,
      draggedItemKey: null,
      currentPage: null,
      isIciDisplayGrouped: true,
      currentSetting: EMPTY_SETTING,
      draftSettings: EMPTY_SETTING,
   },
})
@Injectable()
export class AgentGreetingSettingsState implements NgxsOnInit {
   constructor(private store: Store, private usersService: UsersService) {}

   @Selector([AgentGreetingSettingsState])
   public static wasValuePersisted(state: AgentGreetingSettingsStateModel): boolean {
      return state.wasValuePersisted;
   }

   @Selector([AgentGreetingSettingsState])
   public static filterValue(state: AgentGreetingSettingsStateModel): string {
      return state.filterValue;
   }

   @Selector([AgentGreetingSettingsState])
   public static currentPage(state: AgentGreetingSettingsStateModel): PageAction {
      return state.currentPage;
   }

   @Selector([AgentGreetingSettingsState])
   private static isInEditing(state: AgentGreetingSettingsStateModel) {
      return state.isInEditing;
   }

   @Selector([AgentGreetingSettingsState])
   public static draggedItemKey(state: AgentGreetingSettingsStateModel) {
      return state.draggedItemKey;
   }

   @Selector([AgentGreetingSettingsState])
   public static isIciDisplayGrouped(state: AgentGreetingSettingsStateModel) {
      return state.isIciDisplayGrouped;
   }

   @Selector([AgentGreetingSettingsState])
   private static currentSetting(state: AgentGreetingSettingsStateModel) {
      return state.currentSetting;
   }

   @Selector([AgentGreetingSettingsState])
   private static draftSettings(state: AgentGreetingSettingsStateModel) {
      return state.draftSettings;
   }

   @Selector([AgentGreetingSettingsState.draftSettings])
   private static addedWaves(draftSettings: AgentGreetingSettings) {
      const test = draftSettings.agentGreetingWaves.filter((wave) => wave?.id < 0);
      return <AgentGreetingWaveRemoteDto[]>test;
   }

   @Selector([AgentGreetingSettingsState.currentSetting, AgentGreetingSettingsState.draftSettings])
   private static addedSettings(currentSetting: AgentGreetingSettings, draftSettings: AgentGreetingSettings) {
      const removedSettings: AgentGreetingItem[] = _.differenceWith(draftSettings.agentGreetingItems, currentSetting.agentGreetingItems, _.isEqual);
      const greetings = removedSettings.map(
         (greetingItem) =>
            <AgentGreetingRemoteDto>{
               assignment: <AgentGreetingAssignmentRemoteDto>{
                  iciId: greetingItem.key.iciId,
                  queueId: greetingItem.key.queueId,
               },
               waveId: greetingItem.waveId,
            }
      );
      return greetings;
   }

   @Selector([AgentGreetingSettingsState.currentSetting, AgentGreetingSettingsState.draftSettings])
   private static removedSettings(currentSetting: AgentGreetingSettings, draftSettings: AgentGreetingSettings) {
      const removedSettings: AgentGreetingItem[] = _.differenceWith(currentSetting.agentGreetingItems, draftSettings.agentGreetingItems, _.isEqual);
      const greetings = removedSettings.map(
         (greetingItem) =>
            <AgentGreetingRemoteDto>{
               assignment: <AgentGreetingAssignmentRemoteDto>{
                  iciId: greetingItem.key.iciId,
                  queueId: greetingItem.key.queueId,
               },
               waveId: null,
            }
      );
      return greetings;
   }

   @Selector([AgentGreetingSettingsState.filterValue])
   public static separatedFilterValues(filterValue: string): string[] {
      if (_.isNil(filterValue)) {
         return;
      }
      return _(filterValue.trim()).split(' ').value();
   }

   @Selector([AgentGreetingSettingsState.isInEditing, AgentGreetingSettingsState.currentSetting, AgentGreetingSettingsState.draftSettings])
   private static editableSettings(isInEditing: boolean, currentSetting: AgentGreetingSettings, draftSettings: AgentGreetingSettings) {
      return isInEditing ? draftSettings : currentSetting;
   }

   @Selector([AgentGreetingSettingsState.editableSettings])
   public static agentGreetingItems(state: AgentGreetingSettings): AgentGreetingItem[] {
      return state.agentGreetingItems;
   }

   @Selector([AgentGreetingSettingsState.agentGreetingItems])
   public static agentGreetingByWave(items: AgentGreetingItem[]) {
      return (waveId: number) => {
         const orderedSkills = _.orderBy(
            items.filter((item) => item?.waveId === waveId),
            (item) => item.key.queueId,
            ['asc']
         );
         return _.orderBy(orderedSkills, (item) => item.key.queueId === SkillConstants.OVERALL_SKILL_ID, ['desc']);
      };
   }

   @Selector([AgentGreetingSettingsState.agentGreetingItems])
   public static agentGreetingIciIds(items: AgentGreetingItem[]): number[] {
      return items.map((item) => item.key.iciId);
   }

   @Selector([AgentGreetingSettingsState.agentGreetingIciIds])
   public static uniqueAgentGreetingIciIds(iciIds: number[]): number[] {
      return _(iciIds).uniq().value();
   }

   @Selector([AgentGreetingSettingsState.editableSettings])
   public static agentGreetingWavesFullContent(state: AgentGreetingSettings): AgentGreetingWave[] {
      return state.agentGreetingWaves;
   }

   @Selector([AgentGreetingSettingsState.agentGreetingWavesFullContent])
   public static agentGreetingWaves(waves: AgentGreetingWave[]): AudioFileInfo[] {
      return waves.map(
         (x) =>
            <AudioFileInfo>{
               id: x.id,
               name: x.name,
            }
      );
   }

   @Selector([AgentGreetingSettingsState.agentGreetingWaves])
   public static waveById(waves: AudioFileInfo[]) {
      return (waveId: number) => {
         return waves.find((x) => x.id === waveId);
      };
   }

   @Selector([AgentGreetingSettingsState.agentGreetingWavesFullContent])
   public static agentGreetingWavesMinId(waves: AgentGreetingWave[]) {
      const minWaveId =
         _(waves)
            .map((x) => x?.id ?? 0)
            .min() ?? 0;
      return minWaveId > 0 ? 0 : minWaveId;
   }

   @Selector([AgentGreetingSettingsState.agentGreetingWavesMinId])
   public static agentGreetingWavesNextId(waveMinId: number) {
      return waveMinId - 1;
   }

   @Selector([AgentGreetingSettingsState.agentGreetingItems, AgentGreetingSettingsState.agentGreetingWaves])
   public static agentGreetingConfig(agentGreetingItems: AgentGreetingItem[], agentGreetingWaves: AudioFileInfo[]): AgentGreetingConfig[] {
      return agentGreetingItems.map(
         (greeting) => <AgentGreetingConfig>{ assignment: greeting, wave: agentGreetingWaves.find((wave) => wave.id === greeting.waveId) }
      );
   }

   @Selector([AgentGreetingSettingsState.agentGreetingConfig])
   public static getSkillAgentGreetingConfig(agentGreetingItems: AgentGreetingConfig[]) {
      return (skillId: number) => {
         return _(agentGreetingItems)
            .filter((gr) => gr.assignment.key.queueId === skillId)
            .value();
      };
   }

   @Selector([AgentGreetingSettingsState.getSkillAgentGreetingConfig])
   public static getWavesForSkill(getAgentGreetingConfig: (skillId: number) => AgentGreetingConfig[]) {
      return (skillId: number) => {
         return getAgentGreetingConfig(skillId).map((gr) => gr.wave);
      };
   }

   @Selector([AgentGreetingSettingsState.getWavesForSkill])
   public static getUniqueWaveIds(agentGreetingItems: (skillId: number) => AudioFileInfo[]) {
      return (skillId: number) => {
         return _(agentGreetingItems(skillId).map((wave) => wave.id))
            .uniq()
            .value();
      };
   }

   @Selector([AgentGreetingSettingsState.getUniqueWaveIds])
   private static getIsConfigConflicting(getUniqueWaveIds: (skillId: number) => number[]) {
      return (skillId: number) => {
         return getUniqueWaveIds(skillId).length > 1;
      };
   }

   public static getSkillConfigIsConflicting(skillId: number) {
      return createSelector([AgentGreetingSettingsState.getIsConfigConflicting], (getIsConfigConflicting: (skillId: number) => boolean) => {
         return getIsConfigConflicting(skillId);
      });
   }

   @Selector([AgentGreetingSettingsState.agentGreetingConfig])
   public static getWavesForIci(agentGreetingItems: AgentGreetingConfig[]) {
      return (ici: number) => {
         return _(agentGreetingItems)
            .filter((gr) => gr.assignment.key.iciId === ici)
            .map((gr) => gr.wave)
            .value();
      };
   }

   @Selector([AgentGreetingSettingsState.agentGreetingConfig])
   public static getWaveForSkillAndIci(agentGreetingItems: AgentGreetingConfig[]) {
      return (skillId: number, ici: number) => {
         return _(agentGreetingItems)
            .filter((gr) => gr.assignment.key.queueId === skillId && gr.assignment.key.iciId === ici)
            .map((gr) => gr.wave)
            .head();
      };
   }

   @Selector([AgentGreetingSettingsState.getWaveForSkillAndIci])
   public static getIsWaveAvailableForSkillAndIci(getWave: (skillId: number, ici: number) => AgentGreetingWave) {
      return (skillId: number, ici: number) => {
         return !_.isNil(getWave(skillId, ici));
      };
   }

   public static getWaveBySkillAndIci(skillId: number, ici: number) {
      return createSelector([AgentGreetingSettingsState.getWaveForSkillAndIci], (waveForSkillAndIci: (skillId: number, ici: number) => AgentGreetingWave) => {
         return waveForSkillAndIci(skillId, ici);
      });
   }

   ngxsOnInit(ctx?: StateContext<any>) {
      this.store
         .select(AgentGreetingSettingsState.isInEditing)
         .pipe(
            filter((inEditing) => inEditing),
            tap(() => ctx.dispatch(new SetAgentGreetingsDraftSettings(this.store.selectSnapshot(AgentGreetingSettingsState.currentSetting))))
         )
         .subscribe();

      this.store
         .select(AgentGreetingSettingsState.isInEditing)
         .pipe(
            filter((inEditing) => !inEditing),
            tap(() => ctx.dispatch(new SetAgentGreetingsDraftSettings(EMPTY_SETTING))),
            tap(() => ctx.dispatch(new SetAgentGreetingIsIciDisplayGrouped(true))),
            tap(() => ctx.dispatch(new SetAgentGreetingSkillFilter(null)))
         )
         .subscribe();
   }

   @Action(SetWasValuePersisted)
   public setWasValuePersisted(ctx: StateContext<AgentGreetingSettingsStateModel>, action: SetWasValuePersisted) {
      ctx.patchState({
         wasValuePersisted: action.wasValuePersisted,
      });
   }

   @Action(SetAgentGreetingsIsInEditing)
   public enableAgentGreetingsEditing(ctx: StateContext<AgentGreetingSettingsStateModel>, action: SetAgentGreetingsIsInEditing) {
      ctx.patchState({
         isInEditing: action.isEditable,
      });
   }

   @Action(SetAgentGreetingSkillFilter)
   public SetAgentGreetingSkillFilter(ctx: StateContext<AgentGreetingSettingsStateModel>, action: SetAgentGreetingSkillFilter) {
      ctx.patchState({
         filterValue: action.filter,
      });
   }

   @Action(SetAgentGreetingPage)
   public setAgentGreetingPage(ctx: StateContext<AgentGreetingSettingsStateModel>, action: SetAgentGreetingPage) {
      ctx.patchState({
         currentPage: action.currentPage,
      });
   }

   @Action(SetAgentGreetingDraggedItemKey)
   public setAgentGreetingDraggedItemKey(ctx: StateContext<AgentGreetingSettingsStateModel>, action: SetAgentGreetingDraggedItemKey) {
      ctx.patchState({
         draggedItemKey: action.draggedItemKey,
      });
   }

   @Action(SetAgentGreetingIsIciDisplayGrouped)
   public setAgentGreetingIsIciDisplayGrouped(ctx: StateContext<AgentGreetingSettingsStateModel>, action: SetAgentGreetingIsIciDisplayGrouped) {
      ctx.patchState({
         isIciDisplayGrouped: action.isIciDisplayGrouped,
      });
   }

   @Action(SetAgentGreetingsDraftSettings)
   public setAgentGreetingsDraftSettings(ctx: StateContext<AgentGreetingSettingsStateModel>, action: SetAgentGreetingsDraftSettings) {
      ctx.patchState({
         draftSettings: action.settings,
      });
   }

   @Action(SetAgentGreetingsSettings)
   public setAgentGreetingsSettings(ctx: StateContext<AgentGreetingSettingsStateModel>, action: SetAgentGreetingsSettings) {
      ctx.patchState({
         currentSetting: action.settings,
      });
   }

   @Action(UpdateAgentGreetingItems)
   public updateAgentGreetingItems(ctx: StateContext<AgentGreetingSettingsStateModel>, action: UpdateAgentGreetingItems) {
      const settings = _.cloneDeep(this.store.selectSnapshot(AgentGreetingSettingsState.editableSettings));
      if (this.store.selectSnapshot(AgentGreetingSettingsState.isInEditing)) {
         ctx.dispatch(new SetAgentGreetingsDraftSettings({ agentGreetingItems: action.items, agentGreetingWaves: settings.agentGreetingWaves }));
      } else {
         ctx.dispatch(new SetAgentGreetingsSettings({ agentGreetingItems: action.items, agentGreetingWaves: settings.agentGreetingWaves }));
      }
   }

   @Action(UpdateGreetingWaves)
   public updateGreetingWaves(ctx: StateContext<AgentGreetingSettingsStateModel>, action: UpdateGreetingWaves) {
      const settings = _.cloneDeep(this.store.selectSnapshot(AgentGreetingSettingsState.editableSettings));
      if (this.store.selectSnapshot(AgentGreetingSettingsState.isInEditing)) {
         ctx.dispatch(new SetAgentGreetingsDraftSettings({ agentGreetingItems: settings.agentGreetingItems, agentGreetingWaves: action.waves }));
      } else {
         ctx.dispatch(new SetAgentGreetingsSettings({ agentGreetingItems: settings.agentGreetingItems, agentGreetingWaves: action.waves }));
      }
   }

   @Action(AddGreetingWave)
   public addGreetingWave(ctx: StateContext<AgentGreetingSettingsStateModel>, action: AddGreetingWave) {
      const waves = _.cloneDeep(this.store.selectSnapshot(AgentGreetingSettingsState.agentGreetingWavesFullContent));
      waves.push(action.wave);
      ctx.dispatch(new UpdateGreetingWaves(waves));
   }

   @Action(RemoveGreetingWave)
   public removeGreetingWave(ctx: StateContext<AgentGreetingSettingsStateModel>, action: RemoveGreetingWave) {
      const waves = _.cloneDeep(this.store.selectSnapshot(AgentGreetingSettingsState.agentGreetingWavesFullContent));
      _.remove(waves, action.wave);
      ctx.dispatch(new UpdateGreetingWaves(waves));
   }

   @Action(AddGreetingItem)
   public addGreetingItem(ctx: StateContext<AgentGreetingSettingsStateModel>, action: AddGreetingItem) {
      const items = _.cloneDeep(this.store.selectSnapshot(AgentGreetingSettingsState.agentGreetingItems));
      items.push(action.item);
      ctx.dispatch(new UpdateAgentGreetingItems(items));
   }

   @Action(AddGreetingItems)
   public addGreetingItems(ctx: StateContext<AgentGreetingSettingsStateModel>, action: AddGreetingItems) {
      const items = _.cloneDeep(this.store.selectSnapshot(AgentGreetingSettingsState.agentGreetingItems));
      const newDifferingEntries = action.items.filter(
         (item) => !items.map((x) => `${x?.key?.iciId}-${x?.key?.queueId}`).includes(`${item?.key?.iciId}-${item?.key?.queueId}`)
      );
      items.push(...newDifferingEntries);
      ctx.dispatch(new UpdateAgentGreetingItems(items));
   }

   @Action(RemoveGreetingItem)
   public removeGreetingItem(ctx: StateContext<AgentGreetingSettingsStateModel>, action: RemoveGreetingItem) {
      const items = _.cloneDeep(this.store.selectSnapshot(AgentGreetingSettingsState.agentGreetingItems));
      _.remove(items, action.item);
      ctx.dispatch(new UpdateAgentGreetingItems(items));
   }

   @Action(RemoveGreetingItems)
   public removeGreetingItems(ctx: StateContext<AgentGreetingSettingsStateModel>, action: RemoveGreetingItems) {
      const items = _.cloneDeep(this.store.selectSnapshot(AgentGreetingSettingsState.agentGreetingItems));
      ctx.dispatch(new UpdateAgentGreetingItems(_.differenceWith(items, action.items, _.isEqual)));
   }

   @Action(UpdateAgentGreetingsFromServer)
   public updateAgentGreetingsFromServer(ctx: StateContext<AgentGreetingSettingsStateModel>, action: UpdateAgentGreetingsFromServer) {
      const newGreetings = action.agentGreetings.map((gr) => {
         return { key: gr.assignment, waveId: gr.waveId };
      });
      const settings = _.cloneDeep(this.store.selectSnapshot(AgentGreetingSettingsState.currentSetting));
      if (this.store.selectSnapshot(AgentGreetingSettingsState.isInEditing)) {
         ctx.dispatch(new SetAgentGreetingsDraftSettings({ agentGreetingItems: newGreetings, agentGreetingWaves: settings.agentGreetingWaves }));
      }
      ctx.dispatch(new SetAgentGreetingsSettings({ agentGreetingItems: newGreetings, agentGreetingWaves: settings.agentGreetingWaves }));
      ctx.dispatch(new SetWasValuePersisted(true));
   }

   @Action(UpdateAgentGreetingWavesFromServer)
   public updateAgentGreetingWavesFromServer(ctx: StateContext<AgentGreetingSettingsStateModel>, action: UpdateAgentGreetingWavesFromServer) {
      const settings = _.cloneDeep(this.store.selectSnapshot(AgentGreetingSettingsState.currentSetting));
      if (this.store.selectSnapshot(AgentGreetingSettingsState.isInEditing)) {
         ctx.dispatch(new SetAgentGreetingsDraftSettings({ agentGreetingItems: settings.agentGreetingItems, agentGreetingWaves: action.agentGreetingWaves }));
      }
      ctx.dispatch(new SetAgentGreetingsSettings({ agentGreetingItems: settings.agentGreetingItems, agentGreetingWaves: action.agentGreetingWaves }));
   }

   @Action(PersistAgentGreetings)
   public persistAgentGreetings(ctx: StateContext<AgentGreetingSettingsStateModel>, action: PersistAgentGreetings) {
      const addedSettings = _.cloneDeep(this.store.selectSnapshot(AgentGreetingSettingsState.addedSettings));
      const removedSettings = _.cloneDeep(this.store.selectSnapshot(AgentGreetingSettingsState.removedSettings));
      const addedWaves = _.cloneDeep(this.store.selectSnapshot(AgentGreetingSettingsState.addedWaves));
      const wasValuePersisted = _.cloneDeep(this.store.selectSnapshot(AgentGreetingSettingsState.wasValuePersisted));

      const greetings = concat(addedSettings, removedSettings);

      if (_.isEmpty(greetings) || !_.some(greetings) || !wasValuePersisted) {
         console.debug('Agent greeting configuration not changed');
         return ctx.dispatch(new SetAgentGreetingsIsInEditing(false));
      }

      const agentGreetingsRequestData = <UpdateAgentGreetingsRequestData>{
         agentGreetings: greetings,
         addedWaves: addedWaves,
      };

      const draftSetting = _.cloneDeep(this.store.selectSnapshot(AgentGreetingSettingsState.draftSettings));
      ctx.dispatch(
         new SetAgentGreetingsSettings({
            agentGreetingItems: draftSetting.agentGreetingItems,
            agentGreetingWaves: draftSetting.agentGreetingWaves.map((x) => <AgentGreetingWave>{ id: x.id, name: x.name, content: null }),
         })
      );
      ctx.dispatch(new SetWasValuePersisted(false));
      ctx.dispatch(new SetAgentGreetingsIsInEditing(false));

      return this.usersService.updateAgentGreetings(agentGreetingsRequestData);
   }
}
